var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Value } from '@sinclair/typebox/value';
import { getWithRetries, HttpError, postWithRetries } from "./http.mjs";
import { logger } from "./log.mjs";
import { createRandomId, objectEntries } from "./utils.mjs";
import { checkoutPageUrlHeaderName, sdkVersionHeaderName, countryCodeHeaderName, currencyHeaderName, languageHeaderName, siteUrlHeaderName, atlasSessionIdHeaderName, pspHeaderName, } from "./constants.mjs";
import { createActionMethods } from "./actionMethods.mjs";
import { completeFreePurchaseResponseSchema } from "./cart/completePurchase.mjs";
import { saveCustomerDataToLocalStorage, setCustomerDataFromLocalStorageIfNeeded } from "./customer.mjs";
import { CURRENT_VERSION } from "./currentVersion.mjs";
import { cartResponseSchema, cartUpdateResponseSchema, maybeCartResponseSchema, myCartsResponseSchema } from "./cart/index.mjs";
export const getAdditionalHeaders = (state) => ({
    [sdkVersionHeaderName]: CURRENT_VERSION,
    [countryCodeHeaderName]: state.countryCode,
    [currencyHeaderName]: state.currency,
    [languageHeaderName]: state.language,
    [siteUrlHeaderName]: state.siteUrl,
    [checkoutPageUrlHeaderName]: state.checkoutPageUrl,
    [atlasSessionIdHeaderName]: state.atlasSessionId,
    [pspHeaderName]: state.psp,
});
const broadcastChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('checkout-sdk') : undefined;
export const createActionsInstance = (state) => {
    const onCommandComplete = {};
    const whenCommandCompletePromise = (command) => {
        const openPromise = createCommandPromise(command);
        onCommandComplete[command.id] = openPromise;
        return openPromise.promise;
    };
    let latestPendingWorkId = 0;
    let commandQueue = [];
    const workQueue = [];
    broadcastChannel === null || broadcastChannel === void 0 ? void 0 : broadcastChannel.addEventListener('message', (event) => {
        if (event.data.type !== 'cart-updated') {
            return;
        }
        if (event.data.cartId === state.id && event.data.cartVersion !== state.version) {
            if (event.data.countryCode !== state.countryCode || event.data.currency !== state.currency || event.data.psp !== state.psp) {
                // We need to do this first before reloading the cart, because if country or currency was
                // changed in another tab/window and we call loadCart() we'll revert the country/currency change
                // in this tab/window. Which will cause an infinite loop since the other tab/window will do the same
                // thing.
                state.update({ countryCode: event.data.countryCode, currency: event.data.currency, psp: event.data.psp });
            }
            const incomingSDKVersion = getAdditionalHeaders(state)['X-SDK-Version'];
            // If the SDK version is different between tabs, then we throw a custom set error and tell the user to reload the page.
            // in development you need to manually change CURRENT_VERSION to test this behavior since its updated on build
            if (incomingSDKVersion !== CURRENT_VERSION) {
                state.onUnexpectedError(`SDK version mismatch between tabs, please reload the page ('${incomingSDKVersion}' vs '${CURRENT_VERSION}')`, {
                    errorType: 'sdkVersionMismatch',
                });
                return;
            }
            loadCart({ reloadAfterEvent: true }).catch((err) => {
                logger.error({ err }, 'Failed to reload cart after broadcast');
            });
        }
    });
    const scheduleWork = (action) => __awaiter(void 0, void 0, void 0, function* () {
        if (workQueue.length > 0) {
            // If there's already work scheduled, we wait for it to finish before scheduling new work.
            yield Promise.all(workQueue);
        }
        const workId = Math.random();
        latestPendingWorkId = workId;
        const workPromise = (() => __awaiter(void 0, void 0, void 0, function* () {
            const idAndVersionBeforeWork = `${state.id}-${state.version}`;
            if (!state.isLoading) {
                state.update({ isLoading: true });
            }
            const response = yield action();
            // If no more work has been scheduled during the time the action was running, we know
            // that the action was the latest one and we can set isLoading to false.
            if (latestPendingWorkId === workId) {
                state.update({ isLoading: false });
            }
            if (response) {
                processCartUpdateResponse(response);
            }
            const idAndVersionAfterWork = `${state.id}-${state.version}`;
            // If the id and version of the cart stayed the same it means it didn't change so there's no
            // need to broadcast an update event.
            if (idAndVersionBeforeWork !== idAndVersionAfterWork) {
                broadcastChannel === null || broadcastChannel === void 0 ? void 0 : broadcastChannel.postMessage({
                    type: 'cart-updated',
                    cartVersion: state.version,
                    cartId: state.id,
                    countryCode: state.countryCode,
                    currency: state.currency,
                    psp: state.psp,
                });
            }
            return response;
        }))();
        workQueue.push(workPromise);
        workPromise.finally(() => {
            const index = workQueue.indexOf(workPromise);
            if (index >= 0) {
                workQueue.splice(index, 1);
            }
        });
        return yield workPromise;
    });
    const processCartUpdateResponse = (response) => {
        if (Value.Check(cartUpdateResponseSchema, response)) {
            for (const [commandId, workComplete] of objectEntries(onCommandComplete)) {
                const commandResponse = response.commands.find((command) => command.commandId === commandId);
                if (commandResponse) {
                    workComplete.resolve(commandResponse);
                    delete onCommandComplete[commandId];
                }
                else {
                    logger.error({ commandId, commands: response.commands }, 'Command not found in response');
                }
            }
        }
    };
    const rejectPendingCommands = (error, commandIds) => {
        for (const [key, workComplete] of Object.entries(onCommandComplete)) {
            if (commandIds && !commandIds.includes(key)) {
                continue;
            }
            workComplete.reject(error);
            delete onCommandComplete[key];
        }
    };
    const processCommands = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!commandQueue.length) {
            return;
        }
        const commandsToSend = commandQueue;
        commandQueue = [];
        const request = {
            commands: commandsToSend,
        };
        try {
            const searchParams = new URLSearchParams();
            if (state.checkout) {
                searchParams.set('expand', 'checkout');
            }
            searchParams.set('isNew', state.isNew.toString());
            // Even though it's possible to send to update towards the exact version of the cart that we have,
            // we want to target the current version. The customer might be working with the same cart in multiple tabs
            // or devices and we want to make sure that the latest changes are applied.
            const version = 'current';
            const response = yield postWithRetries(`/carts/${state.id}/${version}${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`, state.checkoutApiBaseUrl, request, cartUpdateResponseSchema, state.token, getAdditionalHeaders(state));
            // We might have cleared the cart while a request was in flight, and if that's the case
            // we don't want to update the state with the response.
            if (response.cart.id === state.id) {
                state.update(Object.assign(Object.assign({}, response.cart), { isNew: false }));
                return response;
            }
            else {
                rejectPendingCommands(new Error('Cart was cleared while a request was in flight'));
            }
        }
        catch (err) {
            handleUpdateCartError(err, commandsToSend);
        }
    });
    const handleUpdateCartError = (err, commandsToSend) => {
        if (is404Error(err) && !state.isNew) {
            logger.error({ err, state }, 'Server claimed cart not found, attempting to recover cart');
            // We change the id because the server might claim it doesn't exist when in fact it does but belongs to a different user.
            // This should only happen if there's a bug related to authentication but in either way we don't want
            // the customer to lose their cart.
            state.update({ isNew: true, id: createRandomId(), version: createRandomId() });
            commandQueue.push({
                id: createRandomId(),
                command: 'recoverCart',
                cart: JSON.parse(JSON.stringify(state)),
            });
            // We retry the commands against a new cart instead
            commandQueue.push(...commandsToSend);
        }
        else {
            logger.error({ err, commands: commandsToSend }, 'Failed to process commands');
            rejectPendingCommands(err instanceof Error ? err : new Error('Failed to process queue'), commandsToSend.map((c) => c.id));
        }
    };
    const processQueue = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield scheduleWork(processCommands);
        }
        catch (err) {
            rejectPendingCommands(err instanceof Error ? err : new Error('Failed to process queue'));
            logger.error({ err }, 'Failed to process queue');
        }
    });
    let debounce = undefined;
    const addAndProcessCommand = (command) => {
        const workCompletePromise = whenCommandCompletePromise(command).then((resp) => {
            var _a, _b;
            return (Object.assign(Object.assign({}, resp), { errorCode: (_a = resp.errorCode) !== null && _a !== void 0 ? _a : undefined, translatedErrorCode: (_b = state.translateApiMessage(resp.errorCode)) !== null && _b !== void 0 ? _b : resp.translatedErrorCode }));
        });
        commandQueue.push(command);
        // To avoid sending multiple requests in a short period of time, we debounce the processQueue function.
        // Eg if someone presses the + button multiple times in a short period of time, we only send one request.
        clearTimeout(debounce);
        debounce = setTimeout(processQueue, 100);
        // We don't know when this command will actually be processed, so we return a promise that resolves
        // when all the scheduled work is done.
        return workCompletePromise;
    };
    const loadCart = (...args_1) => __awaiter(void 0, [...args_1], void 0, function* ({ includeCheckoutData, cartId, reloadAfterEvent } = {}) {
        includeCheckoutData = includeCheckoutData !== null && includeCheckoutData !== void 0 ? includeCheckoutData : !!state.checkout;
        yield scheduleWork(() => __awaiter(void 0, void 0, void 0, function* () {
            // Even though it's possible to send to update towards the exact version of the cart that we have,
            // we want to target the current version. The customer might be working with the same cart in multiple tabs
            // or devices and we want to make sure that the latest changes are applied.
            const version = 'current';
            try {
                const response = yield sendLoadCartRequest(cartId !== null && cartId !== void 0 ? cartId : state.id, version, includeCheckoutData, reloadAfterEvent !== null && reloadAfterEvent !== void 0 ? reloadAfterEvent : false);
                // We might have cleared the cart while a request was in flight, and if that's the case
                // we don't want to update the state with the response.
                if (response.cart.id === state.id) {
                    state.update(Object.assign({}, response.cart));
                    return response;
                }
                else {
                    rejectPendingCommands(new Error('Cart was cleared while a request was in flight'));
                }
            }
            catch (error) {
                if (is404Error(error)) {
                    if (!state.isNew) {
                        logger.error({ error, state }, 'Server claimed cart not found, recovering cart');
                        // We change the id because the server might claim it doesn't exist when in fact it does but belongs to a different user.
                        // This should only happen if there's a bug related to authentication but in either way we don't want
                        // the customer to lose their cart.
                        state.update({ isNew: true, id: createRandomId(), version: createRandomId() });
                        commandQueue.push({
                            id: createRandomId(),
                            command: 'recoverCart',
                            cart: JSON.parse(JSON.stringify(state)),
                        });
                    }
                    return undefined;
                }
                else {
                    throw error;
                }
            }
        }));
    });
    const sendLoadCartRequest = (cartId, version, includeCheckoutData, reloadAfterEvent) => __awaiter(void 0, void 0, void 0, function* () {
        const searchParams = new URLSearchParams();
        if (includeCheckoutData) {
            searchParams.set('expand', 'checkout');
        }
        const isNewError = !(state.isNew === true || state.isNew === false);
        if (isNewError) {
            logger.error({
                cartId,
                version,
                includeCheckoutData: includeCheckoutData ? true : false,
                reloadAfterEvent,
                isNew: String(state.isNew),
            }, 'SDK Error: isNew is neither false nor true');
        }
        const isNew = isNewError ? true : state.isNew;
        searchParams.set('isNew', isNew.toString());
        searchParams.set('reloadAfterEvent', reloadAfterEvent.toString());
        return yield getWithRetries(`/carts/${cartId !== null && cartId !== void 0 ? cartId : state.id}/${version}${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`, state.checkoutApiBaseUrl, cartResponseSchema, state.token, getAdditionalHeaders(state));
    });
    let recaptchaLoader = undefined;
    const actions = Object.assign(Object.assign({ reload() {
            return __awaiter(this, void 0, void 0, function* () {
                return loadCart();
            });
        },
        setOnBeforePurchase(onBeforePurchase) {
            state.onBeforePurchase = onBeforePurchase;
        },
        setRecaptchaLoader(loader) {
            recaptchaLoader = loader;
        },
        getRecaptchaToken() {
            return __awaiter(this, void 0, void 0, function* () {
                if (!recaptchaLoader) {
                    return null;
                }
                return yield recaptchaLoader();
            });
        },
        /**
         * Call this method when the user navigates to the checkout page or if you need
         * to display the checkout page or data for some other reason.
         * After calling this method, all subsequent calls to the cart will include the checkout data.
         */
        loadCheckout() {
            return __awaiter(this, void 0, void 0, function* () {
                return loadCart({ includeCheckoutData: true });
            });
        } }, createActionMethods(state, addAndProcessCommand)), { setCompletePurchaseHandler(handler) {
            state.completePurchaseHandler = handler;
            return () => {
                if (state.completePurchaseHandler === handler) {
                    state.completePurchaseHandler = undefined;
                }
            };
        },
        clearCartAndShowOrderConfirmation(orderId) {
            return __awaiter(this, void 0, void 0, function* () {
                const maxWait = new Promise((resolve) => setTimeout(resolve, 10 * 1000));
                const orderConfirmationRendered = state.onShowOrderConfirmation(orderId);
                yield Promise.race([maxWait, orderConfirmationRendered]);
                state.clear();
                // Note! It's important that we don't await this call, because this function is called as part of completePurchase() which is
                // called as part of the work scheduling. If we await this call, we will create a deadlock where the work scheduling is waiting for this call to finish,
                // and this call is waiting for the work scheduling to finish.
                setCustomerDataFromLocalStorageIfNeeded(state, actions).catch((err) => {
                    logger.error({ err }, 'Failed to set customer data from local storage after clearing cart');
                });
            });
        },
        /**
         * This is the main entry point for completing a purchase for payment providers where we render/control the buy button.
         * For other payment providers the purchase will be completed using the payment provider's own checkout interactive
         * flow which will notify us when the purchase is completed (eg Stripe).
         */
        completePurchase() {
            return __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                const proceedWithPurchase = (_b = (_a = state.onBeforePurchase) === null || _a === void 0 ? void 0 : _a.call(state)) !== null && _b !== void 0 ? _b : true;
                if (!proceedWithPurchase) {
                    throw new Error('onBeforePurchase cancelled the purchase');
                }
                validateStateForCompletePurchase(state);
                if (((_d = (_c = state.checkout) === null || _c === void 0 ? void 0 : _c.payment) === null || _d === void 0 ? void 0 : _d.$type) === 'free') {
                    return yield scheduleWork(() => completeFreePurchase(state, actions));
                }
                return yield scheduleWork(() => {
                    return callCompletePurchaseHandler(state);
                });
            });
        } });
    initializeFromServer(state, actions, loadCart).catch((err) => {
        logger.error({ err }, 'Failed to initialize state from server');
    });
    state.onChange((newState) => {
        var _a, _b;
        if (((_b = (_a = newState.checkout) === null || _a === void 0 ? void 0 : _a.payment) === null || _b === void 0 ? void 0 : _b.$type) === 'alreadyPaid') {
            actions.clearCartAndShowOrderConfirmation(newState.checkout.payment.orderId);
        }
    });
    return actions;
};
const callCompletePurchaseHandler = (state) => __awaiter(void 0, void 0, void 0, function* () {
    if (!state.completePurchaseHandler) {
        throw new Error('Complete purchase handler is missing, must call setCompletePurchaseHandler() before calling completePurchase(). This is a bug in the SDK.');
    }
    // NOTE! It's important that nothing async happens before this line, because the handler might
    // might require the call of this function to happen synchronously as part of a user interaction. If
    // we await something before this line, it might cause the handler to not work as expected.
    const result = yield state.completePurchaseHandler();
    // We return unexpectedError here if the handler didn't return any response. A handler that doesn't return
    // a response should only happen when the handler will redirect the user, and if the redirect didn't happen
    // it's an unexpected error;
    if (result) {
        return result;
    }
    logger.error({ handler: state.completePurchaseHandler }, "Complete purchase handler did not return a response, it should have redirected the user if it doesn't return a response");
    return { $type: 'unexpectedError' };
});
const validateStateForCompletePurchase = (state) => {
    if (state.isLoading) {
        throw new Error('Cannot call completePurchase() while another operation is in progress');
    }
    if (state.completePurchaseReadyState !== 'ready') {
        throw new Error('Cannot call completePurchase() before the state.completePurchaseReadyState is "ready"');
    }
};
const completeFreePurchase = (state, actions) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const request = {
            recaptchaToken: yield actions.getRecaptchaToken(),
            trackingCookies: state.trackingCookies,
        };
        const response = yield postWithRetries(`/carts/${state.id}/${state.version}/complete-free-purchase`, state.checkoutApiBaseUrl, request, completeFreePurchaseResponseSchema, state.token, getAdditionalHeaders(state));
        actions.clearCartAndShowOrderConfirmation(response.orderId);
        return { $type: 'noAction', orderId: response.orderId };
    }
    catch (error) {
        console.error('Failed to execute complete purchase', error);
        return { $type: 'unexpectedError' };
    }
});
const initializeFromServer = (state, actions, loadCart) => __awaiter(void 0, void 0, void 0, function* () {
    state.onChange(saveCustomerDataToLocalStorage);
    if (!state.isNew) {
        yield loadCart();
    }
    else {
        yield findAndLoadCart(state, loadCart);
    }
    yield setCustomerDataFromLocalStorageIfNeeded(state, actions);
});
const findAndLoadCart = (state, loadCart) => __awaiter(void 0, void 0, void 0, function* () {
    const key = 'migratedAtlasCart';
    const migratedAtlasCart = localStorage.getItem(key);
    if (!migratedAtlasCart) {
        yield loadAtlasCart(state);
        localStorage.setItem(key, 'true');
    }
    else {
        try {
            const { carts } = yield getWithRetries('/carts/mine', state.checkoutApiBaseUrl, myCartsResponseSchema, state.token, getAdditionalHeaders(state));
            if (carts.length > 0) {
                yield loadCart({ includeCheckoutData: !!state.checkout, cartId: carts[0].cartId });
            }
        }
        catch (err) {
            logger.error({ err }, 'Failed to load cart');
        }
    }
});
const loadAtlasCart = (state) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const { cart } = yield getWithRetries('/carts/legacy', state.checkoutApiBaseUrl, maybeCartResponseSchema, state.token, getAdditionalHeaders(state));
        if (cart) {
            state.update(Object.assign(Object.assign({}, cart), { isNew: false }));
        }
    }
    catch (err) {
        if (is404Error(err)) {
            // This is expected, no legacy cart exist
        }
        else {
            logger.error({ err }, 'Failed to load legacy cart');
        }
    }
});
const createCommandPromise = (command) => {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
    });
    return { resolve: (response) => resolve(Object.assign({ command }, response)), reject: reject, promise };
};
const is404Error = (error) => {
    return error instanceof HttpError && error.status === 404;
};
//# sourceMappingURL=actions.js.map